import { Trans, t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import * as Device from "expo-device";
import * as Notifications from "expo-notifications";
import { useSetAtom } from "jotai";
import { useLayoutEffect, useState } from "react";
import { Linking, Platform, ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { ToggleSwitch } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display/Loader";
import { BellIconSlash } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { VStack } from "@whoosh/design-system/src/components/primitive/Stack/VStack";

import { isVerifyingEmailOrPhoneAtom } from "../atoms/stores";
import { useSetUserNotificationPreferencesLegacy } from "../hooks/useSetUserNotificationPreferencesLegacy";
import { MyBookings } from "./MyBookings";
import { MyEventUpdates } from "./MyEventUpdates";
import { ManageNotificationsQuery } from "./__generated__/ManageNotificationsQuery.graphql";

type ManageNotificationsPropTypes = {
  queryRef: PreloadedQuery<ManageNotificationsQuery>;
  setIsOpen: (value: boolean) => void;
};

export type PreferencesType = {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  bookingUpdatesEnabled: boolean;
  bookingRemindersEnabled: boolean;
  eventUpdatesEnabled: boolean;
  eventRemindersEnabled: boolean;
};

export const ManageNotifications = ({
  queryRef,
  setIsOpen,
}: ManageNotificationsPropTypes) => {
  const data = usePreloadedQuery(ManageNotificationsQueryString, queryRef);
  const userId = data?.session?.user?.id ?? "";

  const notificationPreferences = data?.session?.user
    ?.notificationPreferences ?? {
    emailEnabled: false,
    smsEnabled: false,
    pushEnabled: false,
    bookingUpdatesEnabled: false,
    bookingRemindersEnabled: false,
    eventUpdatesEnabled: false,
    eventRemindersEnabled: false,
  };

  const isEmailVerified = data?.session?.user?.isEmailVerified ?? false;
  const isPhoneVerified = data?.session?.user?.isPhoneVerified ?? false;

  const [preferences, setPreferences] = useState<PreferencesType>({
    ...notificationPreferences,
  });

  const { showToast } = useToast();

  const onUpdateCallback = () => {
    setIsOpen(false);
    showToast({
      description: t`Your notification preferences have been updated successfully.`,
      semanticStyle: "success",
      toastId: "user-notification-preferences-update",
    });
  };

  const onErrorCallback = () => {
    showToast({
      description: t`Woops! There was an error saving your preferences.`,
      semanticStyle: "error",
      toastId: "user-notification-preferences-error",
    });
  };

  const [
    updateUserNotificationPreferencesLegacy,
    isUpdatingUserNotificationPreferencesLegacy,
  ] = useSetUserNotificationPreferencesLegacy(
    onUpdateCallback,
    onErrorCallback
  );

  const handleChange = (name: keyof PreferencesType) => {
    setPreferences((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSave = () => {
    updateUserNotificationPreferencesLegacy({
      userId: userId,
      preferences: preferences,
    });
  };

  const setVerifyingPhoneOrEmail = useSetAtom(isVerifyingEmailOrPhoneAtom);

  const [hasPermission, setHasPermission] = useState(true);

  useLayoutEffect(() => {
    if (Device.isDevice && Platform.OS !== "web") {
      async function getPermissionsAsync() {
        const { status } = await Notifications.getPermissionsAsync();
        if (status === "denied") {
          setHasPermission(false);
        }
        if (status === "granted") {
          setHasPermission(true);
        }
      }
      getPermissionsAsync();
    }
  });

  const disableChannels =
    !preferences.bookingRemindersEnabled &&
    !preferences.bookingUpdatesEnabled &&
    !preferences.eventRemindersEnabled &&
    !preferences.eventUpdatesEnabled;
  return (
    <ScrollView style={{ flex: 1 }}>
      {isUpdatingUserNotificationPreferencesLegacy ? <Loader /> : null}
      <VStack gap={6}>
        <Text variant="mainBaseMedium">
          <Trans>Send me notifications about</Trans>
        </Text>
        <MyBookings preferences={preferences} handleChange={handleChange} />
        <MyEventUpdates preferences={preferences} handleChange={handleChange} />

        <Text variant="mainBaseMedium">
          <Trans>Where will you receive these notifications</Trans>
        </Text>
        <VStack gap={3}>
          <VStack>
            <HStack justifyContent={"space-between"} alignItems="center">
              <Text variant="mainSmall">
                <Trans>Email</Trans>
              </Text>
              {isEmailVerified ? (
                <ToggleSwitch
                  name={"emailEnabled"}
                  onToggle={() => handleChange("emailEnabled")}
                  isChecked={preferences.emailEnabled}
                  isDisabled={disableChannels}
                  accessibilityLabel={t`Toggle email notifications`}
                />
              ) : (
                <Button
                  size={"tiny"}
                  variant={"secondary"}
                  onPress={() => {
                    setVerifyingPhoneOrEmail("email");
                  }}
                  accessibilityLabel={t`Verify Now button`}
                >
                  {t`Verify Now`}
                </Button>
              )}
            </HStack>
            {!isEmailVerified ? (
              <Text variant="mainTiny" color="text-secondary">
                <Trans>We need a verified email.</Trans>
              </Text>
            ) : null}
          </VStack>
          <VStack>
            <HStack justifyContent={"space-between"} alignItems="center">
              <Text variant="mainSmall">
                <Trans>SMS</Trans>
              </Text>
              {isPhoneVerified ? (
                <ToggleSwitch
                  name={"smsEnabled"}
                  onToggle={() => handleChange("smsEnabled")}
                  isChecked={preferences.smsEnabled}
                  isDisabled={disableChannels || !hasPermission}
                  accessibilityLabel={t`Toggle SMS notifications`}
                />
              ) : (
                <Button
                  size={"tiny"}
                  variant={"secondary"}
                  onPress={() => {
                    setVerifyingPhoneOrEmail("phone");
                  }}
                  accessibilityLabel={t`Verify Now button`}
                >
                  {t`Verify Now`}
                </Button>
              )}
            </HStack>
            {!isPhoneVerified ? (
              <Text variant="mainTiny" color="text-secondary">
                <Trans>We need a verified phone.</Trans>
              </Text>
            ) : (
              <Text variant="mainSmallMedium" color="text-secondary">
                <Trans>
                  By enabling SMS notifications, you agree to receive text
                  messages from Whoosh. Message & data rates may apply. Message
                  frequency varies.
                </Trans>
              </Text>
            )}
          </VStack>
          {Platform.OS !== "web" ? (
            <HStack justifyContent={"space-between"} alignItems="center">
              <Text variant="mainSmall">
                <Trans>Push</Trans>
              </Text>
              <ToggleSwitch
                name="pushEnabled"
                onToggle={() => handleChange("pushEnabled")}
                isChecked={preferences.pushEnabled}
                isDisabled={disableChannels || !hasPermission}
              />
            </HStack>
          ) : null}
          {Platform.OS !== "web" && !hasPermission ? (
            <VStack bg="surface-secondary" borderRadius="4" p={6} gap={4}>
              <BellIconSlash />
              <Text variant="mainBaseSemiBold">{t`Notification for this app are disabled`}</Text>
              <Text variant="mainSmallMedium">{t`To receive push notifications, you will have to change your notification settings for the Whoosh app.`}</Text>
              <Button
                variant="white"
                size="tiny"
                onPress={() => {
                  Linking.openSettings();
                }}
                accessibilityLabel={t`Go to Settings button`}
              >{t`Go to Settings`}</Button>
            </VStack>
          ) : null}
        </VStack>
        <Button
          accessibilityLabel={t`Save Preferences button`}
          onPress={handleSave}
          isLoading={isUpdatingUserNotificationPreferencesLegacy}
        >{t`Save Preferences`}</Button>
      </VStack>
    </ScrollView>
  );
};

export const ManageNotificationsQueryString = graphql`
  query ManageNotificationsQuery {
    session {
      ...InitialStepFragment_Session
      user {
        id
        ... on MemberUser {
          isEmailVerified
          isPhoneVerified
          notificationPreferences {
            emailEnabled
            smsEnabled
            bookingUpdatesEnabled
            bookingRemindersEnabled
            eventUpdatesEnabled
            eventRemindersEnabled
            pushEnabled
          }
        }
      }
      ...ClubLogoNotificationsFragment_session
    }
  }
`;
